import { createVehicleShape } from 'shared/map/domain/vehicleSymbols/vehicleShape';
import { useMapStyleStore } from 'shared/mapStyles/stores/mapStyleStore';
import { storeToRefs } from 'pinia';
import type { VehicleContext } from 'shared/mapStyles/domain/vehicleContext';
import { useRafFn } from '@vueuse/core';
import { useVehicleStore } from '../../vehicles/stores/vehicleStore';
import type { Vehicle } from '../../vehicles/domain/vehicle';
import type { MapStyle } from '../../mapStyles/domain/mapStyle';
import { watch, type Ref } from 'vue';
import OLMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type { Geometry } from 'ol/geom';
import MultiPolygon from 'ol/geom/MultiPolygon';
import type Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON.js';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Text from 'ol/style/Text';
import Stroke from 'ol/style/Stroke';
import { Color } from '../../common/domain/color';

export interface VehiclesLayerOptions {
  mapStyle?: Ref<MapStyle>;
}

export function useVehiclesLayer(options?: VehiclesLayerOptions) {
  const styleStore = useMapStyleStore();
  const vehicleStore = useVehicleStore();
  const geojson = new GeoJSON();

  let renderScheduled = true;
  const viewModels = new Map<string, VehicleViewModel>();
  const hiddenVehicleIds = new Set<string>();

  const secondSinceFormatter = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  });

  const selectedStyle = getMapStyle();

  vehicleStore.changed.on((ev) => {
    if (ev.action === 'removed') {
      deleteVehicle(ev.id);
    } else {
      mergeVehicle(ev.data);
    }
  });

  for (const vehicle of vehicleStore.vehicles) {
    mergeVehicle(vehicle);
  }

  watch(selectedStyle, () => scheduleRenderAll());
  watch(vehicleStore, () => scheduleRenderAll());

  return {
    attach,
    mergePosition,
    hiddenVehicles,
  };

  function getMapStyle() {
    if (options && options.mapStyle) return options.mapStyle;
    const { selectedStyle } = storeToRefs(styleStore);
    return selectedStyle;
  }

  function hiddenVehicles(action: (vehicleIds: Set<string>) => void) {
    action(hiddenVehicleIds);
    scheduleRenderAll();
  }

  function scheduleRenderAll() {
    for (const viewModel of viewModels.values()) {
      viewModel.renderScheduled = true;
    }

    renderScheduled = true;
  }

  function deleteVehicle(id: string) {
    const viewModel = viewModels.get(id);
    if (!viewModel) return;
    viewModel.deleteScheduled = true;
    viewModel.renderScheduled = true;
    renderScheduled = true;
  }

  function mergeVehicle(data: Vehicle) {
    const viewModel = getOrCreateViewModel(data.id);

    viewModel.vehicle = data;

    const position = data.lastPosition;

    if (position) {
      viewModel.position = {
        latitude: position.latitude,
        longitude: position.longitude,
        heading: position.heading,
        timestamp: new Date(position.clientTimestampUTC).getTime(),
      };
    } else {
      viewModel.position = null;
    }

    viewModel.renderScheduled = true;
    renderScheduled = true;
  }

  function mergePosition(vehicleId: string, data: VehiclePosition) {
    const viewModel = getOrCreateViewModel(vehicleId);

    viewModel.position = data;

    viewModel.renderScheduled = true;
    renderScheduled = true;
  }

  function getOrCreateViewModel(id: string) {
    let viewModel = viewModels.get(id);
    if (!viewModel) {
      viewModel = {
        id: id,

        vehicle: null,
        position: null,

        renderScheduled: true,
        deleteScheduled: false,

        properties: {
          color: '#ffffff',
          label: '',
          opacity: 0.5,
          outlineColor: '#000000',
          vehicleId: id,
        },

        feature: null,
      };
      viewModels.set(id, viewModel);
    }
    return viewModel;
  }

  function attach(map: OLMap) {
    const source = createLayer(map);

    useRafFn(() => {
      if (!renderScheduled) return;
      renderScheduled = false;

      for (const viewModel of viewModels.values()) {
        render(source, viewModel);
        if (viewModel.renderScheduled) {
          renderScheduled = true;
        }
      }
    });

    return source;

    function render(source: VectorSource<Feature<Geometry>>, viewModel: VehicleViewModel) {
      if (!viewModel.renderScheduled) return;

      const position = viewModel.position;
      const vehicle = viewModel.vehicle;

      const timeSinceNewPositionInMilliseconds = Date.now() - (position?.timestamp ?? 0);
      const secondsSinceNewPosition = timeSinceNewPositionInMilliseconds / 1000;

      const hidden =
        !position ||
        !vehicle ||
        secondsSinceNewPosition >= 600 ||
        hiddenVehicleIds.has(viewModel.id);

      if (hidden) {
        if (viewModel.feature) {
          source.removeFeature(viewModel.feature);
          viewModel.feature = null;
        }

        if (viewModel.deleteScheduled) {
          viewModels.delete(viewModel.id);
        }

        return;
      }

      const coordinate = {
        longitude: position.longitude,
        latitude: position.latitude,
        heading: position.heading ?? 0,
      };

      const properties = viewModel.properties;

      const jsonFeature = createVehicleShape({
        coordinate,
        heading: coordinate.heading,
        properties: properties,
        symbol: vehicle.symbol,
      });

      let feature = viewModel.feature;

      if (feature) {
        feature.setGeometry(geojson.readGeometry(jsonFeature.geometry));
      } else {
        jsonFeature.id = viewModel.id;
        feature = geojson.readFeature(jsonFeature) as Feature<Geometry>;
        source.addFeature(feature);
        viewModel.feature = feature;
      }

      let newLabel = properties.label;
      let newColor = properties.color;
      let newOutlineColor = properties.outlineColor;

      const newOpacity = Math.max(
        Math.min(1 - timeSinceNewPositionInMilliseconds / 60000, 1.0),
        0.2,
      );

      if (30 < secondsSinceNewPosition && secondsSinceNewPosition < 60) {
        newLabel = secondSinceFormatter.format(secondsSinceNewPosition) + 's';
      } else {
        newLabel = '';
      }

      const vehicleStyle = selectedStyle.value?.vehicle;

      if (vehicleStyle) {
        const context: VehicleContext = {
          color: vehicle.color ?? '#fafafa',
          name: vehicle.name,
          type: vehicle.type,
        };

        newColor = vehicleStyle.color(context);
      }

      properties.label = newLabel;
      properties.color = newColor;
      properties.opacity = newOpacity;
      properties.outlineColor = newOutlineColor;

      feature.setProperties({
        label: newLabel,
        color: newColor,
        opacity: newOpacity,
        outlineColor: newOutlineColor,
      });

      viewModel.renderScheduled = secondsSinceNewPosition < 60;
    } // end render
  }
}

function createLayer(map: OLMap) {
  const source = new VectorSource();
  const layer = new VectorLayer({
    source,
    style: (feature) => {
      const properties = feature.getProperties();
      const label = properties['label'] as string;
      const color = properties['color'];
      const opacity = properties['opacity'];
      const outlineColor = properties['outlineColor'];
      const polygonNames = properties['polygonNames'] as string[] | undefined;
      const colorConfig = properties['colorConfig'] as Record<string, { type: 'dynamic' | 'fixed'; color?: string }> | undefined;

      const geometry = feature.getGeometry();

      // Check if it's a MultiPolygon (vehicle with multiple parts)
      if (geometry?.getType() === 'MultiPolygon') {
        const multiPolygon = geometry as MultiPolygon;
        const polygons = multiPolygon.getPolygons();

        // Create a style for each polygon
        const styles = polygons.map((_: any, index: number) => {
          let fillColor = color; // Default to dynamic vehicle color

          // Use colorConfig if available
          if (polygonNames && colorConfig) {
            const polygonName = polygonNames[index];
            const config = polygonName ? colorConfig[polygonName] : undefined;
            
            if (config) {
              if (config.type === 'fixed' && config.color) {
                fillColor = config.color;
              }
              // If type is 'dynamic', use the default vehicle color (already set)
            }
          } else {
            // Fallback to old hardcoded logic if no colorConfig available
            const isBody = index === 4 || index === 5;
            fillColor = isBody ? color : '#000000';
          }

          return new Style({
            geometry: polygons[index],
            fill: new Fill({
              color: Color.setAlpha(fillColor, opacity),
            }),
            stroke: new Stroke({
              color: Color.setAlpha(outlineColor, opacity),
            }),
          });
        });

        // Add label to the first polygon only
        if (label) {
          styles[2]!.setText(
            new Text({
              text: label,
            }),
          );
        }

        return styles;
      }

      // Fallback for single polygon (shouldn't happen with new implementation)
      const style = new Style({
        fill: new Fill({
          color: Color.setAlpha(color, opacity),
        }),
        stroke: new Stroke({
          color: Color.setAlpha(outlineColor, opacity),
        }),
      });

      if (label) {
        style.setText(
          new Text({
            text: label,
          }),
        );
      }

      return style;
    },
    updateWhileAnimating: true,
    updateWhileInteracting: true,
  });
  map.addLayer(layer);

  return source;
}

export interface VehicleViewModel {
  id: string;
  vehicle: Vehicle | null;
  position: VehiclePosition | null;

  renderScheduled: boolean;
  deleteScheduled: boolean;

  properties: VehicleProperties;

  feature: Feature<Geometry> | null;
}

export interface VehicleProperties {
  label: string;
  color: string;
  outlineColor: string;
  opacity: number;
  vehicleId: string;
}

export interface VehiclePosition {
  latitude: number;
  longitude: number;
  heading?: number;
  timestamp: number;
}
